/**
 * Thinking Indicator Component
 * Persistent visual indicator with active/inactive states.
 *
 * Two presentations, selected by the `thinking-indicator--waiting` modifier class:
 * - Waiting (class present): animated EnCouple logo mark + cycling text, bare (no
 *   background). Shown while awaiting an LLM reply that will stream. Bars hidden.
 * - Bars (class absent): shimmer bars only. Shown while an assistant reply streams
 *   in, and for non-streaming waits. This is the default presentation.
 *
 * The waiting → bars flip is driven by `enterStreamingPhase()`, called from the
 * SSE renderer's `onBlockRender` whenever an assistant block renders. The flip
 * is re-entrant per turn: tool-pause sites re-arm the waiting phase mid-turn
 * (Case B) and the next rendered block flips it back.
 *
 * Active/Inactive:
 * - Active: animations running (mark spins / bars shimmer, text cycles in waiting).
 * - Inactive: hidden via opacity fade.
 *
 * Text Cycling (waiting phase only):
 * - Stage 1: Initial processing (Thinking, Pondering, etc.)
 * - Stage 2: Deeper analysis (Examining patterns, Connecting dots, etc.)
 * - Stage 3: Formulating response (Composing response, etc.)
 * - Stage 4: Extended wait/overflow (Still thinking, Almost there, etc.)
 *
 * Usage:
 *   const indicator = new ThinkingIndicator(messagesContainer);
 *   indicator.show(undefined, { waiting: true }); // logo mark + cycling text
 *   indicator.enterStreamingPhase();              // flip to shimmer bars
 *   indicator.show();                             // bars only (non-streaming wait)
 *   indicator.deactivate();                       // fade out and stop animations
 *
 * REVERT to the previous shimmer-bars-only design: see the runbook at
 * frontend-user/docs/thinking-indicator-revert.md (the quick path is a two-line
 * kill-switch in ModeOrchestrator — no code deletion required).
 */

import { EncoupleThinkingMark } from './EncoupleThinkingMark.js';
import { thinkingGroups } from '../constants/thinkingGroups.js';

/** Square size (px) of the logo mark in the waiting phase. */
const MARK_SIZE = 20;

/** Default cycling pool — the registry's generic group (stages 1-4). */
const DEFAULT_PHRASES = thinkingGroups.generic.phrases;

/**
 * Text cycling configuration
 */
const TEXT_CYCLE_CONFIG = {
  minDuration: 1000,        // Minimum time before switching (ms)
  maxDuration: 3000,        // Maximum time before switching (ms)
  overflowThreshold: 15000  // Time before entering overflow stage (ms)
};

export class ThinkingIndicator {
  /**
   * @param {HTMLElement} messagesContainer - The messages container to append to
   */
  constructor(messagesContainer) {
    this.messagesContainer = messagesContainer;
    this.element = null;
    this.textElement = null;
    this.markHost = null; // host element for the waiting-phase logo mark
    this.mark = null;     // active EncoupleThinkingMark instance, or null

    // Text cycling state
    this.textCycleTimerId = null;
    this.textCycleStartTime = null;
    this.currentStage = 1;
    this.phrases = DEFAULT_PHRASES;

    // Thinking-Line Theater ordered queue (thinking-lines-header-plan §2.2):
    // when set, the next N text-cycle advances show these lines in order
    // instead of a random stage phrase. Null when empty/inactive.
    this._orderedLines = null;
    this._orderedIndex = 0;

    // Deactivation state - tracks pending deactivation timeout to allow cancellation.
    // The resolver is held alongside it so a cancelling activate() can still settle the
    // promise returned by deactivate() (callers chain action-bar rendering off it).
    this.deactivationTimeoutId = null;
    this.deactivationResolve = null;
    // The in-flight bars-phase teardown promise, shared by every deactivate()
    // call that lands while it is pending. Without this, a second call starts a
    // second 400ms timer; activate() only cancels the latest, and the orphan
    // fires and re-hides an indicator that is covering live work.
    this._pendingDeactivation = null;
  }

  /**
   * Create the indicator HTML element
   * @returns {HTMLElement}
   */
  createIndicator() {
    const indicator = document.createElement('div');
    indicator.className = 'thinking-indicator';

    indicator.innerHTML = `
      <div class="thinking-indicator__content">
        <div class="thinking-indicator__top-row">
          <div class="thinking-indicator__mark"></div>
          <div class="thinking-indicator__text">
            <span class="thinking-indicator__text-content"></span><span class="thinking-indicator__ellipsis"></span>
          </div>
        </div>
        <div class="thinking-indicator__bars">
          <div class="thinking-indicator__bar"></div>
          <div class="thinking-indicator__bar"></div>
          <div class="thinking-indicator__bar"></div>
        </div>
      </div>
    `;

    return indicator;
  }

  /**
   * Get a random duration between min and max bounds
   * @returns {number} Duration in milliseconds
   */
  getRandomDuration() {
    const { minDuration, maxDuration } = TEXT_CYCLE_CONFIG;
    return Math.floor(Math.random() * (maxDuration - minDuration + 1)) + minDuration;
  }

  /**
   * Get a random phrase from the specified stage
   * @param {number} stage - Stage number (1-4)
   * @returns {string} Random phrase from that stage
   */
  getRandomPhrase(stage) {
    const stageKey = `stage${stage}`;
    const phrases = this.phrases[stageKey] || this.phrases.stage1;
    return phrases[Math.floor(Math.random() * phrases.length)];
  }

  /**
   * Determine the current stage based on elapsed time
   * @returns {number} Stage number (1-4)
   */
  calculateStage() {
    if (!this.textCycleStartTime) return 1;

    const elapsed = Date.now() - this.textCycleStartTime;
    const { overflowThreshold } = TEXT_CYCLE_CONFIG;

    // If past overflow threshold, use stage 4
    if (elapsed >= overflowThreshold) {
      return 4;
    }

    // Divide remaining time into 3 equal stages
    const stageTime = overflowThreshold / 3;
    const stage = Math.floor(elapsed / stageTime) + 1;
    return Math.min(stage, 3);
  }

  /**
   * Advance to the next phrase (called by timer).
   *
   * Thinking-Line Theater (§2.2): when an ordered-lines queue is active, it
   * takes priority over the random stage pool — one line per advance, in
   * order (generic pool phrase already shown by startTextCycle() is line 0;
   * this method shows lines[0], lines[1], lines[2]...). Once the queue is
   * exhausted, the last line HOLDS (no further advance) until
   * `overflowThreshold` (15s total elapsed), at which point the queue is
   * permanently discarded and normal stage-4 escalation resumes.
   */
  advanceText() {
    // Calculate what stage we should be in based on time
    const targetStage = this.calculateStage();

    // Only advance forward, never regress
    if (targetStage > this.currentStage) {
      this.currentStage = targetStage;
    }

    if (this._orderedLines) {
      const elapsed = Date.now() - this.textCycleStartTime;
      const { overflowThreshold } = TEXT_CYCLE_CONFIG;

      if (elapsed >= overflowThreshold) {
        // Overflow: permanently discard the queue and fall through to the
        // normal stage-4 pool below (never a second Haiku call, never a
        // silent permanent hold that would defeat calculateStage()).
        this.clearOrderedLines();
      } else if (this._orderedIndex < this._orderedLines.length) {
        const line = this._orderedLines[this._orderedIndex];
        this._orderedIndex += 1;
        this.setText(line);

        if (this._orderedIndex >= this._orderedLines.length) {
          // Queue exhausted: hold this last line until the overflow boundary
          // rather than the normal random cadence.
          const remaining = overflowThreshold - elapsed;
          this.textCycleTimerId = setTimeout(() => {
            this.advanceText();
          }, Math.max(remaining, 0));
        } else {
          this.textCycleTimerId = setTimeout(() => {
            this.advanceText();
          }, this.getRandomDuration());
        }
        return;
      }
    }

    // Get and display a random phrase from current stage
    const phrase = this.getRandomPhrase(this.currentStage);
    this.setText(phrase);

    // Schedule next advance
    this.textCycleTimerId = setTimeout(() => {
      this.advanceText();
    }, this.getRandomDuration());
  }

  /**
   * Queue lines for sequential display after the current phrase's hold:
   * generic (already showing) → lines[0] → lines[1] → lines[2]; then the
   * last line holds until `overflowThreshold` (15s), after which the queue is
   * permanently discarded and normal stage-4 escalation cycling resumes.
   *
   * A no-op if: `lines` is empty (documented — generic cycling continues
   * unaffected), the indicator has already left the waiting phase (streaming
   * started or never entered it), or no text cycle is currently running.
   *
   * @param {string[]} lines - Up to 3 theater lines, best-first
   */
  setOrderedLines(lines) {
    if (!Array.isArray(lines) || lines.length === 0) return;
    if (!this.element?.classList.contains('thinking-indicator--waiting')) return;
    if (this.textCycleTimerId === null) return;
    this._orderedLines = lines;
    this._orderedIndex = 0;
  }

  /**
   * Discard any queued ordered lines without touching the current phrase or
   * stage clock. Called by every teardown/phase-flip path
   * (enterStreamingPhase, deactivate) AND explicitly by ChatManager's
   * tool-phase `state:update` branch (N5) — the existing waiting-phase re-arm
   * there (`activateThinkingIndicator`) is an idempotent no-op while already
   * waiting and cannot clear the queue implicitly.
   */
  clearOrderedLines() {
    this._orderedLines = null;
    this._orderedIndex = 0;
  }

  /**
   * Start the text cycling
   * @param {string} [initialPhrase] - Optional first phrase to show instead of a
   *   random stage-1 pick (e.g. the new-session opener). Only the very first
   *   phrase differs — the first advance onward cycles the normal pool, and the
   *   stage clock starts here either way.
   */
  startTextCycle(initialPhrase) {
    // Reset state
    this.currentStage = 1;
    this.textCycleStartTime = Date.now();

    // Show initial phrase immediately
    this.setText(
      typeof initialPhrase === 'string' && initialPhrase
        ? initialPhrase
        : this.getRandomPhrase(1)
    );

    // Schedule first advance
    this.textCycleTimerId = setTimeout(() => {
      this.advanceText();
    }, this.getRandomDuration());
  }

  /**
   * Stop the text cycling
   */
  stopTextCycle() {
    if (this.textCycleTimerId !== null) {
      clearTimeout(this.textCycleTimerId);
      this.textCycleTimerId = null;
    }
    this.textCycleStartTime = null;
    this.currentStage = 1;
  }

  /**
   * Set custom phrases for action-specific text cycling. Stages the caller
   * omits are inherited from the generic pool, so a group may supply only
   * stage1/stage2 and keep the generic overflow escalation.
   *
   * If a text cycle is live, the displayed phrase re-renders immediately from
   * the new pool at the current stage — without resetting the stage clock, so
   * escalation continues from the original entry into the waiting phase.
   * @param {Object} phrases - Phrases object with stage1..stage4 arrays (partial OK)
   */
  setPhrases(phrases) {
    this.phrases = { ...DEFAULT_PHRASES, ...phrases };
    if (this.textCycleTimerId !== null) {
      this.setText(this.getRandomPhrase(this.currentStage));
    }
  }

  /**
   * Reset to default phrases
   */
  resetPhrases() {
    this.phrases = DEFAULT_PHRASES;
  }

  /**
   * Tear down the active logo mark, if any. Idempotent — safe to call when no
   * mark exists. Releasing the mark is what lets EncoupleThinkingMark's shared
   * RAF clock idle, so every teardown/transition path must run through here.
   * @private
   */
  _destroyMark() {
    if (this.mark) {
      this.mark.destroy();
      this.mark = null;
    }
  }

  /**
   * Show the thinking indicator (creates if not exists)
   * @param {string} [text] - Optional static text (bars phase only; not shown while
   *   the top row is hidden). Omit for the waiting phase's cycling text.
   * @param {{waiting?: boolean, initialPhrase?: string}} [opts] - `waiting: true`
   *   (with no static text) shows the logo mark + cycling text; otherwise the bars
   *   presentation. `initialPhrase` overrides only the first cycling phrase (fresh
   *   waiting entry only — a re-entrant waiting activate ignores it).
   */
  show(text, opts = {}) {
    // If already visible, just re-activate.
    if (this.element && this.element.parentNode) {
      this.activate(text, opts);
      return;
    }

    // Create and append indicator
    this.element = this.createIndicator();
    this.textElement = this.element.querySelector('.thinking-indicator__text-content');
    this.markHost = this.element.querySelector('.thinking-indicator__mark');
    this.messagesContainer.appendChild(this.element);

    // Activate
    this.activate(text, opts);
  }

  /**
   * Update the displayed text
   * @param {string} text - The new text to display
   */
  setText(text) {
    if (this.textElement) {
      this.textElement.textContent = text;
    }
  }

  /**
   * Re-trigger the bar expand animations by cloning and replacing each bar.
   * CSS animations only play once per element — this resets them. Must run while
   * the bars are visible (no `--waiting`), or the once-only expand won't play.
   */
  restartBarAnimations() {
    if (!this.element) return;
    const bars = this.element.querySelectorAll('.thinking-indicator__bar');
    bars.forEach(bar => {
      const fresh = bar.cloneNode(true);
      bar.parentNode.replaceChild(fresh, bar);
    });
  }

  /**
   * Activate indicator - fade in and start the requested presentation.
   * @param {string} [text] - Optional static text (bars phase). Omit for cycling.
   * @param {{waiting?: boolean, initialPhrase?: string}} [opts] - `waiting: true`
   *   (with no static text) enters the waiting phase (logo mark + cycling text);
   *   otherwise bars. `initialPhrase` overrides only the first cycling phrase on a
   *   fresh waiting entry; the idempotent re-entry path never reaches it.
   */
  activate(text, opts = {}) {
    if (!this.element) return;

    // Cancel any pending deactivation timeout to prevent race condition
    // where deactivate() was called, then activate() called during the 400ms window
    if (this.deactivationTimeoutId !== null) {
      clearTimeout(this.deactivationTimeoutId);
      this.deactivationTimeoutId = null;
      this._pendingDeactivation = null;
      // Settle the cancelled deactivation. Without this the promise returned by
      // deactivate() never resolves and anything chained off it (notably the
      // message action bar) is silently dropped for the rest of the turn.
      if (this.deactivationResolve) {
        this.deactivationResolve();
        this.deactivationResolve = null;
      }
    }

    // Normalize: clear teardown classes from any prior cycle.
    this.element.classList.remove('thinking-indicator--inactive');
    this.element.classList.remove('thinking-indicator--exiting');

    const waiting = opts.waiting === true && text === undefined;

    if (waiting) {
      // Idempotent re-entry: already in the waiting phase with a live mark —
      // leave both the mark and the stage clock untouched. Consumers re-enter
      // here on every 8s heartbeat; rebuilding would restart the logo and reset
      // stage escalation on exactly the longest waits. (The `this.mark` conjunct
      // is defence-in-depth — no current path pairs --waiting with a null mark.)
      if (this.element.classList.contains('thinking-indicator--waiting') && this.mark) {
        return;
      }
      // Waiting phase: logo mark + cycling text, bars hidden.
      this.element.classList.add('thinking-indicator--waiting');
      this._destroyMark(); // never overwrite a live mark (activate() is re-entrant)
      this.mark = new EncoupleThinkingMark(this.markHost, { size: MARK_SIZE });
      this.startTextCycle(opts.initialPhrase);
    } else {
      // Bars phase (default): shimmer bars, no mark. Any static text is set but
      // stays hidden behind the collapsed top row (parity with prior behavior).
      this.element.classList.remove('thinking-indicator--waiting');
      this._destroyMark();
      this.stopTextCycle();
      this.restartBarAnimations();
      if (text !== undefined) {
        this.setText(text);
      }
    }
  }

  /**
   * Flip the waiting phase (logo mark + text) to the bars phase. Called from the
   * SSE renderer's `onBlockRender` whenever an assistant block renders (per block,
   * not per message). No-op if not currently in the waiting phase, so non-streaming
   * turns and repeat block renders cost one classList check.
   */
  enterStreamingPhase() {
    if (!this.element) return;
    if (!this.element.classList.contains('thinking-indicator--waiting')) return;

    this._destroyMark();
    this.stopTextCycle();
    this.clearOrderedLines();
    // Remove --waiting first so the bars are visible, THEN restart their
    // once-only expand animation in a rendered container.
    this.element.classList.remove('thinking-indicator--waiting');
    this.restartBarAnimations();
  }

  /**
   * Deactivate indicator - waiting phase hides immediately; bars phase plays the
   * collapse animation then hides.
   * @returns {Promise<void>} Resolves when deactivation completes
   */
  deactivate() {
    if (!this.element) return Promise.resolve();

    // Tear down the mark synchronously on every path — before any early return
    // and never inside the deferred timeout (a cancelling activate() would skip
    // it and leak the mark into the shared RAF clock).
    this._destroyMark();

    // Same rule for the phrase pool: reset before any early return, so a turn
    // that ends on an action-specific pool cannot leak it into the next turn.
    this.resetPhrases();

    // Same rule for the ordered-lines queue: a turn that ends before its
    // queue was fully consumed (or before enterStreamingPhase ever ran — a
    // pure tool-call turn with no prose) must not leak lines into the next
    // turn's waiting phase.
    this.clearOrderedLines();

    // A teardown already in flight owns this deactivation — hand back its
    // promise instead of arming a second timer. MUST sit below _destroyMark()
    // above: that call is deliberately ahead of every early return, and moving
    // this guard above it would leak the mark into the shared RAF clock.
    if (this.deactivationTimeoutId !== null) {
      return this._pendingDeactivation;
    }

    // If already inactive, resolve immediately
    if (this.element.classList.contains('thinking-indicator--inactive')) {
      return Promise.resolve();
    }

    // Stop text cycling
    this.stopTextCycle();

    // Waiting phase (logo mark + text, no bars visible): hide immediately.
    if (this.element.classList.contains('thinking-indicator--waiting')) {
      this.element.classList.remove('thinking-indicator--waiting');
      this.element.classList.add('thinking-indicator--inactive');
      return Promise.resolve();
    }

    // Bars phase: trigger collapse animation, then hide after it completes.
    this.element.classList.add('thinking-indicator--exiting');

    this._pendingDeactivation = new Promise(resolve => {
      this.deactivationResolve = resolve;
      this.deactivationTimeoutId = setTimeout(() => {
        this.deactivationTimeoutId = null;
        this.deactivationResolve = null;
        this._pendingDeactivation = null;
        // After collapse animation finishes, fully hide
        if (this.element) {
          this.element.classList.remove('thinking-indicator--exiting');
          this.element.classList.add('thinking-indicator--inactive');
        }
        resolve();
      }, 400); // 0.3s collapse + 0.1s stagger buffer
    });

    return this._pendingDeactivation;
  }

  /**
   * Hide and remove the thinking indicator (legacy method for compatibility)
   * Now deactivates instead of removing
   */
  hide() {
    this.deactivate();
  }

  /**
   * Check if indicator is currently visible
   * @returns {boolean}
   */
  isVisible() {
    return this.element !== null && this.element.parentNode !== null;
  }

  /**
   * Check if indicator is currently active (animating)
   * @returns {boolean}
   */
  isActive() {
    return this.element && !this.element.classList.contains('thinking-indicator--inactive');
  }
}
